import { useCallback, useEffect, useState } from "react";
import { HardDrive, MessageCircle, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { API, http, authHeaders, formatApiError, CAREERS_TOKEN } from "../../../lib/api";

const Card = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-md border border-white/10 bg-[#111519] p-6 sm:p-8" data-testid={testid}>
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-emerald-400" />
      <p className="font-display text-2xl">{title}</p>
    </div>
    <div className="mt-5 space-y-4 text-sm text-zinc-300">{children}</div>
  </div>
);

export const IntegrationsTab = ({ onAuthError }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const h = { headers: authHeaders(CAREERS_TOKEN) };

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`${API}/careers/admin/drive/status`, { headers: authHeaders(CAREERS_TOKEN) });
      setS(data ?? null);
    } catch (err) {
      if (err?.response?.status === 401) onAuthError();
    }
  }, [onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (path, msg) => {
    setBusy(true);
    try {
      const { data } = await http.post(`${API}/careers/admin/drive/${path}`, {}, h);
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
        return;
      }
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="integrations-tab">
      <Card icon={HardDrive} title="Google Drive" testid="drive-card">
        {!s && <Loader2 className="h-4 w-4 animate-spin" />}
        {s && !s.configured && (
          <p className="flex gap-2" data-testid="drive-not-configured"><AlertCircle className="h-4 w-4 shrink-0 text-emerald-400 mt-0.5" />Google OAuth credentials haven't been added yet. Once they're added, connect your Drive here. CVs received meanwhile are kept safely and can be synced afterwards.</p>
        )}
        {s?.configured && !s.connected && (
          <>
            <p>Connect the Google account that should receive candidate CVs. They'll be saved to a folder named "{s.folder}".</p>
            <button disabled={busy} onClick={() => act("connect")} className="rounded-full bg-emerald-500 px-5 py-2.5 font-medium text-[#0a0c0e] hover:bg-emerald-400 disabled:opacity-60" data-testid="drive-connect-btn">Connect Google Drive</button>
          </>
        )}
        {s?.connected && (
          <>
            <p className="flex items-center gap-2" data-testid="drive-connected"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Connected{s.account_email ? ` as ${s.account_email}` : ""}. CVs go to "{s.folder}".</p>
            <div className="flex flex-wrap gap-3">
              <button disabled={busy || !s.pending} onClick={() => act("sync", "Syncing pending CVs to Drive")} className="rounded-full bg-emerald-500 px-5 py-2.5 font-medium text-[#0a0c0e] hover:bg-emerald-400 disabled:opacity-50" data-testid="drive-sync-btn">Sync {s.pending} pending CV{s.pending === 1 ? "" : "s"}</button>
              <button disabled={busy} onClick={() => act("disconnect", "Drive disconnected")} className="rounded-full border border-white/15 px-5 py-2.5 hover:border-red-400" data-testid="drive-disconnect-btn">Disconnect</button>
            </div>
          </>
        )}
        {s && !s.connected && s.pending > 0 && <p className="text-zinc-400">{s.pending} CV{s.pending === 1 ? "" : "s"} waiting to be copied to Drive.</p>}
      </Card>
      <Card icon={MessageCircle} title="WhatsApp (Ani)" testid="whatsapp-card">
        <p>Ani is live in the website chat on the Careers pages. WhatsApp isn't connected yet.</p>
        <p className="text-zinc-400">When you connect a WhatsApp number, point its incoming-message webhook to:</p>
        <code className="block break-all rounded bg-black/40 px-3 py-2 font-mono text-xs text-emerald-200" data-testid="whatsapp-webhook-url">{`${process.env.REACT_APP_BACKEND_URL}/api/wingman/whatsapp`}</code>
      </Card>
    </div>
  );
};
